/**
 * gfsSampling.ts
 *
 * Pure (no fetch, no DOM) GFS grid-sampling logic, extracted from gfsData.ts so it can be
 * shared verbatim between the main thread's GfsDataStore (which fetches data over HTTP) and
 * the particle physics Web Worker (see workers/particlePhysicsWorker.ts), which receives the
 * SAME parsed data transferred from the main thread instead of fetching it a second time.
 * Keeping exactly one copy of this sampling math avoids the two call sites' behavior silently
 * drifting apart over time.
 */

export interface VariableBlock {
  scale: number;
  offset: number;
  data: Int16Array;
}

export interface GridInfo {
  numLat: number;
  numLon: number;
  latStart: number;
  lonStart: number;
  latStepDeg: number;
  lonStepDeg: number;
}

export interface LevelData extends GridInfo {
  hpa: number;
  u: VariableBlock;
  v: VariableBlock;
  wz: VariableBlock;
  gh: VariableBlock;
  t: VariableBlock;
}

export interface SurfaceData extends GridInfo {
  field: VariableBlock;
}

export interface WindSample {
  uMs: number;
  vMs: number;
  wMs: number;
  geopotentialHeightM: number;
  temperatureK: number;
}

/** Bilinear-samples a packed variable block at (latDeg, lonDeg in -180..180). No closures are
 *  created here -- this runs up to 10x per sampleWindFromLevels() call, which itself runs once
 *  per particle per frame, so avoiding per-call allocations here matters at scale (tens of
 *  thousands of particles). */
export function sampleBlock(grid: GridInfo, block: VariableBlock, latDeg: number, lonDeg: number): number {
  const lon360 = ((lonDeg % 360) + 360) % 360;
  const rowF = (latDeg - grid.latStart) / grid.latStepDeg;
  const colF = (lon360 - grid.lonStart) / grid.lonStepDeg;

  const row0 = Math.floor(rowF);
  const col0 = Math.floor(colF);
  const rowT = rowF - row0;
  const colT = colF - col0;

  const numLat = grid.numLat;
  const numLon = grid.numLon;
  const clampedRow0 = row0 < 0 ? 0 : row0 >= numLat ? numLat - 1 : row0;
  const row1 = row0 + 1;
  const clampedRow1 = row1 < 0 ? 0 : row1 >= numLat ? numLat - 1 : row1;
  let wrappedCol0 = col0 % numLon;
  if (wrappedCol0 < 0) wrappedCol0 += numLon;
  let wrappedCol1 = (col0 + 1) % numLon;
  if (wrappedCol1 < 0) wrappedCol1 += numLon;

  const data = block.data;
  const scale = block.scale;
  const offset = block.offset;
  const v00 = data[clampedRow0 * numLon + wrappedCol0] * scale + offset;
  const v10 = data[clampedRow0 * numLon + wrappedCol1] * scale + offset;
  const v01 = data[clampedRow1 * numLon + wrappedCol0] * scale + offset;
  const v11 = data[clampedRow1 * numLon + wrappedCol1] * scale + offset;

  const top = v00 + (v10 - v00) * colT;
  const bottom = v01 + (v11 - v01) * colT;
  return top + (bottom - top) * rowT;
}

/**
 * Samples real GFS wind/height/temperature, log-pressure interpolated between the two nearest
 * LOADED levels. `sortedLevelsDescending` must already be sorted surface-first (largest hPa
 * first) -- callers own that sort/cache (see GfsDataStore.getSortedLevels() and the worker's
 * equivalent) since it doesn't need to be redone on every sample.
 */
export function sampleWindFromLevels(
  sortedLevelsDescending: LevelData[],
  latDeg: number,
  lonDeg: number,
  pressureHpa: number
): WindSample {
  if (sortedLevelsDescending.length === 0) {
    return { uMs: 0, vMs: 0, wMs: 0, geopotentialHeightM: 0, temperatureK: 288.15 };
  }

  // Binary search for the bracketing pair -- the array is sorted descending by hpa.
  let lower = sortedLevelsDescending[0]; // larger hPa (closer to surface)
  let upper = sortedLevelsDescending[sortedLevelsDescending.length - 1]; // smaller hPa (higher altitude)
  if (sortedLevelsDescending.length > 1) {
    let lo = 0;
    let hi = sortedLevelsDescending.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >>> 1;
      if (sortedLevelsDescending[mid].hpa >= pressureHpa) {
        lo = mid;
      } else {
        hi = mid;
      }
    }
    lower = sortedLevelsDescending[lo];
    upper = sortedLevelsDescending[hi];
  }
  if (pressureHpa >= lower.hpa) upper = lower;
  if (pressureHpa <= upper.hpa) lower = upper;

  const logLower = Math.log(lower.hpa);
  const logUpper = Math.log(upper.hpa);
  const t = logLower === logUpper ? 0 : (Math.log(pressureHpa) - logLower) / (logUpper - logLower);
  const clampedT = t < 0 ? 0 : t > 1 ? 1 : t;

  const uLower = sampleBlock(lower, lower.u, latDeg, lonDeg);
  const uUpper = lower === upper ? uLower : sampleBlock(upper, upper.u, latDeg, lonDeg);
  const vLower = sampleBlock(lower, lower.v, latDeg, lonDeg);
  const vUpper = lower === upper ? vLower : sampleBlock(upper, upper.v, latDeg, lonDeg);
  const wzLower = sampleBlock(lower, lower.wz, latDeg, lonDeg);
  const wzUpper = lower === upper ? wzLower : sampleBlock(upper, upper.wz, latDeg, lonDeg);
  const ghLower = sampleBlock(lower, lower.gh, latDeg, lonDeg);
  const ghUpper = lower === upper ? ghLower : sampleBlock(upper, upper.gh, latDeg, lonDeg);
  const tLower = sampleBlock(lower, lower.t, latDeg, lonDeg);
  const tUpper = lower === upper ? tLower : sampleBlock(upper, upper.t, latDeg, lonDeg);

  return {
    uMs: uLower + (uUpper - uLower) * clampedT,
    vMs: vLower + (vUpper - vLower) * clampedT,
    wMs: wzLower + (wzUpper - wzLower) * clampedT,
    geopotentialHeightM: ghLower + (ghUpper - ghLower) * clampedT,
    temperatureK: tLower + (tUpper - tLower) * clampedT
  };
}

/** Samples surface MSLP (hPa); 1013.25 (standard sea-level pressure) if surface data isn't loaded yet. */
export function sampleMslpFromSurface(surface: SurfaceData | null, latDeg: number, lonDeg: number): number {
  if (!surface) return 1013.25;
  return sampleBlock(surface, surface.field, latDeg, lonDeg);
}
